/**
 * Legal Agreements Guard
 * Utility functions to check and enforce legal agreements compliance
 */

import { getApiClient } from './apiClient';
import { getCurrentLegalPage } from 'components/legal/legalPagesConfig';
import logger from './logger';

// Cache key for storing compliance result
// NOTE: Client-side caching is for UX optimization only - server enforces security
const COMPLIANCE_CACHE_KEY = 'legal_agreements_compliance_cache';
const API_RESPONSE_CACHE_KEY = 'legal_agreements_api_response_cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes - UX optimization, server enforces security
const API_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes for API response cache - UX optimization

/**
 * Get cached compliance result
 * @param {string} userId - User ID for cache key
 * @param {boolean} allowExpired - If true, return expired cache (for fallback scenarios)
 * @returns {object|null} Cached result or null
 */
const getCachedCompliance = (userId, allowExpired = false) => {
  try {
    const cacheKey = `${COMPLIANCE_CACHE_KEY}_${userId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const { compliant, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;
    
    // Return cached result if still valid OR if expired but allowExpired is true
    if (age < CACHE_TTL_MS || allowExpired) {
      if (age >= CACHE_TTL_MS) {
        logger.debug('LEGAL-GUARD', 'Using expired cached compliance result as fallback', { ageSeconds: Math.round(age / 1000) });
      } else {
        logger.debug('LEGAL-GUARD', 'Using cached compliance result', { ageSeconds: Math.round(age / 1000) });
      }
      return compliant;
    }
    
    // Cache expired and not allowed, remove it
    sessionStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    logger.warn('LEGAL-GUARD', 'Error reading compliance cache', { error: error.message });
    return null;
  }
};

/**
 * Cache compliance result
 * @param {string} userId - User ID for cache key
 * @param {boolean} compliant - Compliance result
 */
const setCachedCompliance = (userId, compliant) => {
  try {
    const cacheKey = `${COMPLIANCE_CACHE_KEY}_${userId}`;
    const cacheData = {
      compliant,
      timestamp: Date.now()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
    logger.debug('LEGAL-GUARD', 'Cached compliance result');
  } catch (error) {
    logger.warn('LEGAL-GUARD', 'Error caching compliance result', { error: error.message });
  }
};

/**
 * Get cached API response
 * @param {string} userId - User ID for cache key
 * @param {boolean} allowExpired - If true, return expired cache (for fallback scenarios)
 * @returns {object|null} Cached API response or null
 */
const getCachedApiResponse = (userId, allowExpired = false) => {
  try {
    const cacheKey = `${API_RESPONSE_CACHE_KEY}_${userId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;
    
    // Return cached response if still valid OR if expired but allowExpired is true
    if (age < API_CACHE_TTL_MS || allowExpired) {
      if (age >= API_CACHE_TTL_MS) {
        logger.debug('LEGAL-GUARD', 'Using expired cached API response as fallback', { ageSeconds: Math.round(age / 1000) });
      } else {
        logger.debug('LEGAL-GUARD', 'Using cached API response', { ageSeconds: Math.round(age / 1000) });
      }
      return data;
    }
    
    // Cache expired and not allowed, remove it
    sessionStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    logger.warn('LEGAL-GUARD', 'Error reading API response cache', { error: error.message });
    return null;
  }
};

/**
 * Cache API response (exported for use in apiClient)
 * @param {string} userId - User ID for cache key
 * @param {object} data - API response data
 */
export const setCachedApiResponse = (userId, data) => {
  try {
    const cacheKey = `${API_RESPONSE_CACHE_KEY}_${userId}`;
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    logger.warn('LEGAL-GUARD', 'Error caching API response', { error: error.message });
  }
};

/**
 * Clear API response cache for user
 * @param {string} userId - User ID for cache key
 */
export const clearCachedApiResponse = (userId) => {
  try {
    const cacheKey = `${API_RESPONSE_CACHE_KEY}_${userId}`;
    sessionStorage.removeItem(cacheKey);
  } catch (error) {
    logger.warn('LEGAL-GUARD', 'Error clearing API response cache', { error: error.message });
  }
};

/**
 * Check if error is a transient network/token error that shouldn't cause redirect
 * @param {Error} error - Error object
 * @returns {boolean} True if error is transient
 */
const isTransientError = (error) => {
  // Check for token acquisition errors
  const isTokenError = error?.message?.includes('block_iframe_reload') ||
                      error?.message?.includes('Token acquisition') ||
                      error?.message?.includes('iframe') ||
                      error?.code === 'block_iframe_reload';
  
  // Check for network errors
  const isNetworkError = error?.message?.includes('Failed to fetch') ||
                        error?.message?.includes('NetworkError') ||
                        error?.message?.includes('timeout') ||
                        error?.message?.includes('Network request failed') ||
                        !navigator.onLine;
  
  // Check for 5xx server errors (transient)
  const isServerError = error?.response?.status >= 500 && error?.response?.status < 600;
  
  return isTokenError || isNetworkError || isServerError;
};

/**
 * Check if user has agreed to all required legal agreements
 * 
 * NOTE: This is a UX optimization only. Server-side enforcement is the security boundary.
 * Client-side checks can be bypassed, but the server will enforce compliance on all API calls.
 * 
 * @param {object} msalInstance - Optional MSAL instance to check authentication
 * @returns {Promise<boolean>} True if all agreements are accepted, or true if user is not authenticated
 */
export const checkLegalAgreementsCompliance = async (msalInstance = null) => {
  // If no MSAL instance provided, try to get it from context
  // But for now, we'll check authentication via the API call error handling
  
  try {
    // Check if user is authenticated before making API call
    // If msalInstance is provided, check accounts first
    let userId = null;
    if (msalInstance) {
      const accounts = msalInstance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        logger.debug('LEGAL-GUARD', 'User is not authenticated, skipping legal agreements check');
        return true; // Allow unauthenticated users to access (they'll be prompted to login by MSAL if needed)
      }
      // Get user ID for caching - try multiple fallbacks
      userId = accounts[0]?.homeAccountId || 
               accounts[0]?.localAccountId || 
               accounts[0]?.username ||
               accounts[0]?.idTokenClaims?.oid ||
               accounts[0]?.idTokenClaims?.sub;
      
      // Only log if we're actually making an API call (not using cache)
      // Don't log user IDs for security
      logger.debug('LEGAL-GUARD', 'Checking compliance for user (cache check first)');
    }
    
    // Check cache first if we have a user ID
    // Only use cached result if it's true (compliant) - never trust cached false
    // This ensures we always re-check non-compliant users
    if (userId) {
      const cached = getCachedCompliance(userId);
      if (cached === true) {
        // Only trust cached compliant results
        logger.debug('LEGAL-GUARD', 'Using cached compliant result');
        return cached;
      }
      // If cached is false or null, check API response cache first
      if (cached === false) {
        logger.debug('LEGAL-GUARD', 'Cached result is false, checking API response cache before re-checking');
      }
      
      // Check if we have a recent API response cache
      const cachedApiResponse = getCachedApiResponse(userId);
      if (cachedApiResponse) {
        // Use cached API response to calculate compliance without making API call
        const agreements = cachedApiResponse;
        // Comprehensive merged user + business agreements - covers all scenarios
        const requiredAgreements = [
          'termsOfService',
          'dataProcessingAgreement',
          'privacyPolicy',
          'endUserLicenseAgreement',
          'acceptableUse'
        ];
        
        // Check if cache has all required fields (if not, it's stale and we should refresh)
        const hasAllFields = requiredAgreements.every(key => key in agreements);
        if (!hasAllFields) {
          logger.debug('LEGAL-GUARD', 'Cached API response missing required fields, will refresh');
          // Clear stale cache and continue to API call
          clearCachedApiResponse(userId);
        } else {
          const allAccepted = requiredAgreements.every(key => {
            const value = agreements[key];
            if (value === undefined || value === null) return false;
            if (typeof value === 'boolean') return value === true;
            if (value && typeof value === 'object') return value.agreed === true;
            return false;
          });
          
          logger.debug('LEGAL-GUARD', 'Using cached API response to calculate compliance');
          return allAccepted;
        }
      }
    }
    
    // Only log "checking" if we're actually making an API call (cache miss)
    if (userId) {
      logger.debug('LEGAL-GUARD', 'Making API call to check compliance (cache miss)');
    }
    
    const apiClient = getApiClient();
    let response;
    
    try {
      // CRITICAL: Force fresh API call to bypass memory cache
      // This ensures we get the latest data, not stale cached responses
      response = await apiClient.getUserLegalAgreements(true);
    } catch (error) {
      logger.warn('LEGAL-GUARD', 'API call failed, checking cache for fallback', { error: error.message });
      
      // On API failure, check for ANY cached data (even expired) as fallback
      if (userId) {
        // First check if we have a cached API response (even if expired)
        const cachedApiResponse = getCachedApiResponse(userId, true);
        if (cachedApiResponse) {
          // Use cached API response even if expired when API call fails
          const agreements = cachedApiResponse;
          const requiredAgreements = [
            'termsOfService',
            'dataProcessingAgreement',
            'privacyPolicy',
            'endUserLicenseAgreement',
            'acceptableUse'
          ];
          
          const allAccepted = requiredAgreements.every(key => {
            const value = agreements[key];
            if (value === undefined || value === null) return false;
            if (typeof value === 'boolean') return value === true;
            if (value && typeof value === 'object') return value.agreed === true;
            return false;
          });
          
          logger.debug('LEGAL-GUARD', 'Using cached API response as fallback (API call failed)');
          return allAccepted;
        }
        
        // If no cached API response, check compliance cache (even expired)
        const cached = getCachedCompliance(userId, true);
        if (cached === true) {
          logger.debug('LEGAL-GUARD', 'Using cached compliant result as fallback (API call failed)');
          return true;
        }
      }
      
      // If no cache available, check if it's a transient error
      if (isTransientError(error)) {
        logger.debug('LEGAL-GUARD', 'Transient error - allowing access to prevent unnecessary modal');
        return true; // Changed: be lenient on transient errors
      }
      
      // For non-transient errors without cache, assume non-compliant
      logger.warn('LEGAL-GUARD', 'API call failed with no cache - assuming non-compliant');
      return false;
    }
    
    // If API call succeeded but returned error response
    if (!response || !response.success || !response.data) {
      logger.warn('LEGAL-GUARD', 'API returned error response, checking cache for fallback');
      
      // Check cache as fallback
      if (userId) {
        const cachedApiResponse = getCachedApiResponse(userId, true);
        if (cachedApiResponse) {
          const agreements = cachedApiResponse;
          const requiredAgreements = [
            'termsOfService',
            'dataProcessingAgreement',
            'privacyPolicy',
            'endUserLicenseAgreement',
            'acceptableUse'
          ];
          
          const allAccepted = requiredAgreements.every(key => {
            const value = agreements[key];
            if (value === undefined || value === null) return false;
            if (typeof value === 'boolean') return value === true;
            if (value && typeof value === 'object') return value.agreed === true;
            return false;
          });
          
          logger.debug('LEGAL-GUARD', 'Using cached API response as fallback (API returned error)');
          return allAccepted;
        }
        
        const cached = getCachedCompliance(userId, true);
        if (cached === true) {
          logger.debug('LEGAL-GUARD', 'Using cached compliant result as fallback (API returned error)');
          return true;
        }
      }
      
      // If we can't fetch agreements and no valid cache, assume they need to be set (non-compliant)
      logger.warn('LEGAL-GUARD', 'Could not fetch legal agreements, assuming non-compliant');
      return false;
    }
    
    // API call succeeded and returned valid data
    if (response && response.success && response.data) {
      const agreements = response.data;
      
      logger.info('LEGAL-GUARD', 'Received API response for compliance check', {
        success: response.success,
        hasData: !!response.data,
        agreementKeys: Object.keys(agreements),
        agreementValues: agreements
      });
      
      // Strictly check that ALL required agreements are accepted (agreed === true)
      // Required agreements: termsOfService, dataProcessingAgreement, privacyPolicy, endUserLicenseAgreement, acceptableUse
      const requiredAgreements = [
        'termsOfService',
        'dataProcessingAgreement',
        'privacyPolicy',
        'endUserLicenseAgreement',
        'acceptableUse'
      ];
      
      // Check each agreement and log which ones are missing/false
      const agreementStatus = {};
      const missingAgreements = [];
      
      const allAccepted = requiredAgreements.every(key => {
        const value = agreements[key];
        
        // Explicitly handle missing, null, or undefined values
        if (value === undefined || value === null) {
          agreementStatus[key] = { status: 'missing', value };
          missingAgreements.push(key);
          return false;
        }
        
        // Handle both boolean and object format
        let isAccepted = false;
        if (typeof value === 'boolean') {
          isAccepted = value === true;
        } else if (value && typeof value === 'object') {
          isAccepted = value.agreed === true;
        }
        
        agreementStatus[key] = { 
          status: isAccepted ? 'accepted' : 'not_accepted', 
          value,
          type: typeof value
        };
        
        if (!isAccepted) {
          missingAgreements.push(key);
        }
        
        return isAccepted;
      });
      
      logger.info('LEGAL-GUARD', 'Compliance check result', {
        allAccepted,
        agreementStatus,
        missingAgreements: missingAgreements.length > 0 ? missingAgreements : 'none'
      });
      
      // CRITICAL FIX: Check if API returned all false (which might indicate an error)
      // If all agreements are false but we have a cached compliant result, trust the cache
      const allFalse = requiredAgreements.every(key => {
        const value = agreements[key];
        return value === false || value === undefined || value === null;
      });
      
      if (allFalse && userId) {
        const cached = getCachedCompliance(userId, true);
        if (cached === true) {
          logger.warn('LEGAL-GUARD', 'API returned all false agreements but user was recently compliant - trusting cache');
          return true;
        }
      }
      
      logger.debug('LEGAL-GUARD', 'Legal agreements compliance check completed', {
        allAccepted,
        requiredAgreementsCount: requiredAgreements.length,
        agreementStatus,
        missingAgreements: missingAgreements.length > 0 ? missingAgreements : 'none',
        rawAgreements: agreements
      });
      
      // Cache both the compliance result AND the API response
      // This prevents repeated API calls even for non-compliant users
      if (userId) {
        // Cache API response (works for both compliant and non-compliant)
        setCachedApiResponse(userId, agreements);
        
        // Only cache compliance boolean if compliant (for quick checks)
        if (allAccepted) {
          setCachedCompliance(userId, allAccepted);
          logger.debug('LEGAL-GUARD', 'Cached compliant result');
        }
      }
      
      return allAccepted;
    }
  } catch (error) {
    // Check if error is due to authentication (401/403)
    const isAuthError = error?.response?.status === 401 || 
                       error?.response?.status === 403 ||
                       error?.message?.includes('401') ||
                       error?.message?.includes('403') ||
                       error?.message?.includes('Unauthorized') ||
                       error?.message?.includes('authentication');
    
    if (isAuthError) {
      logger.debug('LEGAL-GUARD', 'User is not authenticated (auth error), skipping legal agreements check');
      return true; // Allow unauthenticated users to access (they'll be prompted to login by MSAL if needed)
    }
    
    // Check if this is a transient error (token acquisition, network, etc.)
    if (isTransientError(error)) {
      logger.debug('LEGAL-GUARD', 'Transient error checking legal agreements compliance, checking cache', { error: error.message });
      
      // Try to get user ID for cache lookup (with multiple fallbacks)
      let userId = null;
      if (msalInstance) {
        try {
          const accounts = msalInstance.getAllAccounts();
          if (accounts && accounts.length > 0) {
            userId = accounts[0]?.homeAccountId || 
                     accounts[0]?.localAccountId || 
                     accounts[0]?.username ||
                     accounts[0]?.idTokenClaims?.oid ||
                     accounts[0]?.idTokenClaims?.sub;
          }
        } catch (e) {
          logger.warn('LEGAL-GUARD', 'Could not extract user ID for cache lookup', { error: e.message });
        }
      }
      
      // If we have a cached result (even expired), use it to avoid unnecessary redirects
      if (userId) {
        // First check cached API response (even expired)
        const cachedApiResponse = getCachedApiResponse(userId, true);
        if (cachedApiResponse) {
          const agreements = cachedApiResponse;
          const requiredAgreements = [
            'termsOfService',
            'dataProcessingAgreement',
            'privacyPolicy',
            'endUserLicenseAgreement',
            'acceptableUse'
          ];
          
          const allAccepted = requiredAgreements.every(key => {
            const value = agreements[key];
            if (value === undefined || value === null) return false;
            if (typeof value === 'boolean') return value === true;
            if (value && typeof value === 'object') return value.agreed === true;
            return false;
          });
          
          logger.debug('LEGAL-GUARD', 'Using cached API response for transient error - preventing unnecessary modal');
          return allAccepted;
        }
        
        // Fallback to compliance cache (even expired)
        const cached = getCachedCompliance(userId, true);
        if (cached === true) {
          logger.debug('LEGAL-GUARD', 'Using cached compliant result for transient error - preventing unnecessary modal');
          return true;
        }
      }
      
      // For transient errors, be lenient - allow access to prevent redirect loops
      // The user can still access the page, and we'll retry on next check
      logger.debug('LEGAL-GUARD', 'Transient error - allowing access to prevent redirect loop');
      return true;
    }
    
    logger.error('LEGAL-GUARD', 'Error checking legal agreements compliance', { error: error.message });
    
    // For non-transient errors, check cache (even expired) one more time before giving up
    let userId = null;
    if (msalInstance) {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts && accounts.length > 0) {
          userId = accounts[0]?.homeAccountId || 
                   accounts[0]?.localAccountId || 
                   accounts[0]?.username ||
                   accounts[0]?.idTokenClaims?.oid ||
                   accounts[0]?.idTokenClaims?.sub;
        }
      } catch (e) {
        // Ignore errors extracting user ID
      }
    }
    
    if (userId) {
      // First check cached API response (even expired)
      const cachedApiResponse = getCachedApiResponse(userId, true);
      if (cachedApiResponse) {
        const agreements = cachedApiResponse;
        const requiredAgreements = [
          'termsOfService',
          'dataProcessingAgreement',
          'privacyPolicy',
          'endUserLicenseAgreement',
          'acceptableUse'
        ];
        
        const allAccepted = requiredAgreements.every(key => {
          const value = agreements[key];
          if (value === undefined || value === null) return false;
          if (typeof value === 'boolean') return value === true;
          if (value && typeof value === 'object') return value.agreed === true;
          return false;
        });
        
        logger.debug('LEGAL-GUARD', 'Using cached API response for error - preventing modal');
        return allAccepted;
      }
      
      // Fallback to compliance cache (even expired)
      const cached = getCachedCompliance(userId, true);
      if (cached === true) {
        logger.debug('LEGAL-GUARD', 'Using cached compliant result for error - preventing modal');
        return true;
      }
      
      // If user exists but no cache, assume non-compliant (new user)
      logger.warn('LEGAL-GUARD', 'Error for user without cache - assuming non-compliant (likely new user)');
      return false;
    }
    
    // Only return true if we have no user ID (unauthenticated)
    // If there's an error that's not auth-related and not transient, assume non-compliant to be safe
    logger.warn('LEGAL-GUARD', 'Uncertain compliance status due to error - allowing access only if unauthenticated');
    return true; // Only allow if truly unauthenticated
  }
};

/**
 * Get the current legal agreements status
 * @returns {Promise<object>} Legal agreements object with boolean values
 */
export const getLegalAgreementsStatus = async () => {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.getUserLegalAgreements();
    
    if (response && response.success && response.data) {
      return response.data;
    }
    
    // Return default false values if fetch fails
    return {
      termsOfService: false,
      dataProcessingAgreement: false,
      privacyPolicy: false,
      endUserLicenseAgreement: false,
      acceptableUse: false
    };
  } catch (error) {
    logger.error('LEGAL-GUARD', 'Error getting legal agreements status', { error: error.message });
    return {
      termsOfService: false,
      dataProcessingAgreement: false,
      privacyPolicy: false,
      endUserLicenseAgreement: false,
      acceptableUse: false
    };
  }
};

/**
 * Check if the current pathname is a legal page
 * @param {string} pathname - Current route pathname
 * @returns {boolean} True if the path is a legal page
 */
export const isLegalPage = (pathname) => {
  if (!pathname) return false;
  
  // Use the centralized legal pages config to check
  const currentLegalPage = getCurrentLegalPage(pathname);
  return currentLegalPage !== null;
};

/**
 * Check if current route should be protected by legal agreements
 * @param {string} pathname - Current route pathname
 * @returns {boolean} True if route should be protected
 */
export const isProtectedRoute = (pathname) => {
  // CRITICAL: Legal pages should never be protected - users need to read them before agreeing
  if (isLegalPage(pathname)) {
    return false;
  }
  
  // Routes that require legal agreements compliance
  const protectedRoutes = [
    '/',
    '/home',
    '/dashboard',
    '/workspace',
    '/bot-management',
    '/cost-tracking',
    '/subscription',
    '/settings'
  ];
  
  // Routes that are always accessible (don't require legal agreements)
  // These can be language-prefixed (e.g., /en/profile, /de/profile)
  const publicRoutes = [
    '/profile',
    '/payment-profile',
    '/auth',
    '/login',
    '/signup',
    '/terms-of-service',
    '/privacy-policy',
    '/data-processing-agreement',
    '/end-user-license-agreement'
  ];
  
  // Normalize pathname: remove language prefix for matching (e.g., /en/profile -> /profile)
  // Match pattern: /{lang}/... where lang is 2 lowercase letters
  const normalizedPath = pathname.replace(/^\/[a-z]{2}\//, '/');
  
  // Check if it's a public route (check both original and normalized path)
  if (publicRoutes.some(route => pathname.startsWith(route) || normalizedPath.startsWith(route))) {
    return false;
  }
  
  // Check if it's a protected route (check both original and normalized path)
  // Homepage routes: / or /{lang}/ (where lang is 2 lowercase letters)
  if (pathname === '/' || /^\/[a-z]{2}\/?$/.test(pathname)) {
    return true;
  }
  
  return protectedRoutes.some(route => pathname.startsWith(route) || normalizedPath.startsWith(route));
};

/**
 * Get the profile page URL for redirect
 * @returns {string} Profile page URL
 */
export const getProfilePageUrl = () => {
  return '/profile';
};

/**
 * Clear cached compliance result for a user
 * @param {string} userId - User ID (optional, clears all if not provided)
 */
/**
 * Clear compliance cache for user
 * Also clears API response cache
 * @param {string|null} userId - User ID or null to clear all
 */
export const clearComplianceCache = (userId = null) => {
  try {
    if (userId) {
      // Clear both compliance cache and API response cache
      const complianceCacheKey = `${COMPLIANCE_CACHE_KEY}_${userId}`;
      sessionStorage.removeItem(complianceCacheKey);
      clearCachedApiResponse(userId);
      logger.info('LEGAL-GUARD', 'Cleared compliance and API response cache for user', { userId });
    } else {
      // Clear all compliance cache entries and API response cache entries
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith(COMPLIANCE_CACHE_KEY) || key.startsWith(API_RESPONSE_CACHE_KEY)) {
          sessionStorage.removeItem(key);
        }
      });
      logger.info('LEGAL-GUARD', 'Cleared all compliance and API response cache entries');
    }
  } catch (error) {
    logger.warn('LEGAL-GUARD', 'Error clearing compliance cache', { error: error.message });
  }
};
